/**
 * Mortgages & property finance: loans, payments (interest/capital/fees), statements.
 * Finance costs are shown separately from operating expenses.
 */
import { LOAN_TRANSACTION_TYPES, LOAN_TYPES, RATE_TYPES } from "@shared/constants.ts";
import { loanSchema, loanTransactionSchema } from "@shared/schemas/finance.ts";
import { Card, Col, Row, Table } from "react-bootstrap";
import { Link, useNavigate, useParams, useSearchParams } from "react-router";
import { Attachments } from "../../components/Attachments.tsx";
import { AuditHistory } from "../../components/AuditHistory.tsx";
import { EntityForm } from "../../components/forms/EntityForm.tsx";
import {
  DateField,
  MoneyField,
  NumberField,
  SelectField,
  TextAreaField,
  TextField,
} from "../../components/forms/fields.tsx";
import { ModalFormButton } from "../../components/forms/ModalForm.tsx";
import { type Column, DataTable } from "../../components/ui/DataTable.tsx";
import { Icon } from "../../components/ui/Icon.tsx";
import {
  Can,
  ConfirmButton,
  DefinitionList,
  EmptyState,
  IconButtonLink,
  Money,
  PageHeader,
  QueryState,
  Section,
  StatCard,
  StatusBadge,
} from "../../components/ui/kit.tsx";
import { api } from "../../lib/api.ts";
import { useAuth } from "../../lib/auth.tsx";
import { useFormat } from "../../lib/format.ts";
import { toOptions, useLookups } from "../../lib/lookups.ts";
import { useApi, useInvalidateAll } from "../../lib/query.ts";

interface LoanRow {
  id: string;
  propertyId: string;
  lender: string;
  accountReference: string;
  loanType: string;
  originalAmountPence: number | null;
  startDate: string | null;
  termMonths: number | null;
  rateType: string | null;
  interestRateBp: number | null;
  productEndDate: string | null;
  redeemedDate: string | null;
  notes: string;
  addressLine1: string;
  postcode: string;
  totalInterestPence: number;
  totalFeesPence: number;
  totalCapitalPence: number;
  lastPaymentDate: string | null;
}

interface LoanTx {
  id: string;
  transactionType: string;
  transactionDate: string;
  interestPence: number;
  capitalPence: number;
  feesPence: number;
  reference: string;
  notes: string;
}

const rate = (bp: number | null) => (bp === null ? "—" : `${(bp / 100).toFixed(2)}%`);

export function LoanTable({ propertyId }: { propertyId?: string }) {
  const f = useFormat();
  const q = useApi<LoanRow[]>("/api/loans", { propertyId });
  const columns: Column<LoanRow>[] = [
    {
      key: "lender",
      header: "Lender",
      cell: (l) => (
        <>
          <span className="fw-semibold">{l.lender}</span>
          <div className="small text-muted">{f.label(LOAN_TYPES, l.loanType)}</div>
        </>
      ),
    },
    ...(propertyId ? [] : [{ key: "property", header: "Property", cell: (l: LoanRow) => l.addressLine1 }]),
    {
      key: "rate",
      header: "Rate",
      cell: (l) =>
        `${rate(l.interestRateBp)} ${l.rateType ? f.label(RATE_TYPES, l.rateType).toLowerCase() : ""}`,
    },
    { key: "product", header: "Product ends", cell: (l) => f.date(l.productEndDate) },
    {
      key: "interest",
      header: "Interest & fees paid",
      align: "end",
      cell: (l) => <Money pence={l.totalInterestPence + l.totalFeesPence} kind="expense" />,
    },
    {
      key: "status",
      header: "",
      cell: (l) =>
        l.redeemedDate ? <StatusBadge status="ended" label="redeemed" /> : <StatusBadge status="active" />,
    },
  ];
  return (
    <QueryState query={q}>
      {(rows) => (
        <DataTable
          rows={rows}
          columns={columns}
          rowKey={(l) => l.id}
          rowLink={(l) => `/finance/${l.id}`}
          empty={
            <EmptyState
              icon="finance"
              title="No mortgages or loans"
              action={
                <IconButtonLink
                  to={`/finance/new${propertyId ? `?propertyId=${propertyId}` : ""}`}
                  icon="plus"
                >
                  Add mortgage
                </IconButtonLink>
              }
            >
              Record mortgages and loans to track interest and fees, which are reported separately as finance
              costs.
            </EmptyState>
          }
        />
      )}
    </QueryState>
  );
}

export function LoanListPage() {
  return (
    <>
      <PageHeader
        title="Mortgages & finance"
        subtitle="Interest and fees are reported as finance costs, separately from operating expenses"
        actions={
          <Can permission="finance:write">
            <IconButtonLink to="/finance/new" icon="plus">
              Add mortgage / loan
            </IconButtonLink>
          </Can>
        }
      />
      <Card>
        <LoanTable />
      </Card>
    </>
  );
}

export function LoanFormPage() {
  const { id } = useParams();
  const [params] = useSearchParams();
  const lookups = useLookups();
  return (
    <EntityForm
      schema={loanSchema}
      resource="/api/loans"
      id={id}
      title={id ? "Edit mortgage / loan" : "Add mortgage / loan"}
      breadcrumbs={[{ label: "Mortgages & finance", to: "/finance" }, { label: id ? "Edit" : "New" }]}
      defaults={{
        propertyId: params.get("propertyId") ?? "",
        lender: "",
        accountReference: "",
        loanType: "interest_only",
        originalAmountPence: "",
        startDate: "",
        termMonths: "",
        rateType: "fixed",
        interestRatePercent: "",
        productEndDate: "",
        redeemedDate: "",
        notes: "",
      }}
      toForm={(r) => ({
        ...Object.fromEntries(Object.entries(r).map(([k, v]) => [k, v === null ? "" : v])),
        interestRatePercent: r.interestRateBp === null ? "" : ((r.interestRateBp as number) / 100).toFixed(2),
      })}
      redirect={(s) => `/finance/${s.id}`}
      cancelTo={id ? `/finance/${id}` : "/finance"}
    >
      {() => (
        <Row>
          <Col md={6}>
            <SelectField
              name="propertyId"
              label="Property"
              options={toOptions(lookups?.properties)}
              placeholder="Choose a property"
              required
            />
          </Col>
          <Col md={6}>
            <TextField name="lender" label="Lender" required />
          </Col>
          <Col md={4}>
            <SelectField name="loanType" label="Type" options={LOAN_TYPES} />
          </Col>
          <Col md={4}>
            <TextField
              name="accountReference"
              label="Account reference"
              hint="Shown in full only to your team."
            />
          </Col>
          <Col md={4}>
            <MoneyField name="originalAmountPence" label="Amount borrowed" />
          </Col>
          <Col md={3}>
            <DateField name="startDate" label="Start date" />
          </Col>
          <Col md={3}>
            <NumberField name="termMonths" label="Term (months)" min={1} />
          </Col>
          <Col md={3}>
            <SelectField name="rateType" label="Rate type" options={RATE_TYPES} placeholder="—" />
          </Col>
          <Col md={3}>
            <TextField name="interestRatePercent" label="Interest rate (%)" placeholder="e.g. 4.79" />
          </Col>
          <Col md={6}>
            <DateField
              name="productEndDate"
              label="Product / deal ends"
              hint="You'll be reminded 90 days before."
            />
          </Col>
          <Col md={6}>
            <DateField name="redeemedDate" label="Redeemed on" />
          </Col>
          <Col md={12}>
            <TextAreaField name="notes" label="Notes" rows={2} />
          </Col>
        </Row>
      )}
    </EntityForm>
  );
}

function TxFields() {
  return (
    <Row>
      <Col md={6}>
        <SelectField name="transactionType" label="Type" options={LOAN_TRANSACTION_TYPES} />
      </Col>
      <Col md={6}>
        <DateField name="transactionDate" label="Date" required />
      </Col>
      <Col md={4}>
        <MoneyField name="interestPence" label="Interest" />
      </Col>
      <Col md={4}>
        <MoneyField name="capitalPence" label="Capital" hint="Never treated as an expense." />
      </Col>
      <Col md={4}>
        <MoneyField name="feesPence" label="Fees" />
      </Col>
      <Col md={12}>
        <TextField name="reference" label="Reference" />
        <TextAreaField name="notes" label="Notes" rows={2} />
      </Col>
    </Row>
  );
}

export function LoanDetailPage() {
  const { id } = useParams();
  const f = useFormat();
  const navigate = useNavigate();
  const invalidate = useInvalidateAll();
  const { can } = useAuth();
  const q = useApi<LoanRow & { transactions: LoanTx[] }>(`/api/loans/${id}`);
  return (
    <QueryState query={q}>
      {(l) => {
        const interest = l.transactions.reduce((s, t) => s + t.interestPence, 0);
        const fees = l.transactions.reduce((s, t) => s + t.feesPence, 0);
        const capital = l.transactions.reduce((s, t) => s + t.capitalPence, 0);
        return (
          <>
            <PageHeader
              title={`${l.lender} ${f.label(LOAN_TYPES, l.loanType).toLowerCase()}`}
              subtitle={
                <Link to={`/properties/${l.propertyId}`}>
                  {l.addressLine1}, {l.postcode}
                </Link>
              }
              breadcrumbs={[{ label: "Mortgages & finance", to: "/finance" }, { label: l.lender }]}
              actions={
                <>
                  {can("finance:write") && (
                    <ModalFormButton
                      label={
                        <>
                          <Icon name="plus" size={14} /> Record payment
                        </>
                      }
                      buttonSize="md"
                      variant="primary"
                      title="Record payment or fee"
                      schema={loanTransactionSchema}
                      defaults={{
                        transactionType: "payment",
                        transactionDate: new Date().toISOString().slice(0, 10),
                        interestPence: "",
                        capitalPence: "",
                        feesPence: "",
                        reference: "",
                        notes: "",
                      }}
                      onSubmit={(v) => api.post(`/api/loans/${l.id}/transactions`, v)}
                    >
                      {() => <TxFields />}
                    </ModalFormButton>
                  )}
                  <Can permission="finance:write">
                    <IconButtonLink to={`/finance/${l.id}/edit`} icon="edit" variant="outline-primary">
                      Edit
                    </IconButtonLink>
                  </Can>
                  <Can permission="finance:delete">
                    <ConfirmButton
                      size="md"
                      title="Delete this loan?"
                      body="Its payments will also be removed from finance cost reporting."
                      onConfirm={async () => {
                        await api.delete(`/api/loans/${l.id}`);
                        await invalidate();
                        navigate("/finance");
                      }}
                    >
                      Delete
                    </ConfirmButton>
                  </Can>
                </>
              }
            />
            <Row className="g-3 mb-4">
              <Col sm={4}>
                <StatCard
                  label="Interest paid"
                  value={<Money pence={interest} kind="expense" />}
                  tone="expense"
                  hint="Reported as finance costs"
                />
              </Col>
              <Col sm={4}>
                <StatCard label="Fees paid" value={<Money pence={fees} kind="expense" />} />
              </Col>
              <Col sm={4}>
                <StatCard label="Capital repaid" value={<Money pence={capital} />} hint="Not an expense" />
              </Col>
            </Row>
            <Row>
              <Col lg={8}>
                <Section title="Loan details">
                  <DefinitionList
                    items={[
                      ["Account reference", l.accountReference],
                      ["Amount borrowed", f.money(l.originalAmountPence)],
                      ["Start date", f.date(l.startDate)],
                      ["Term", l.termMonths ? `${l.termMonths} months` : "—"],
                      ["Rate", `${rate(l.interestRateBp)} ${f.label(RATE_TYPES, l.rateType)}`],
                      ["Product ends", f.date(l.productEndDate)],
                      ["Redeemed", f.date(l.redeemedDate)],
                      ["Notes", l.notes],
                    ]}
                  />
                </Section>
                <Section title="Payments" flush>
                  {l.transactions.length === 0 ? (
                    <p className="small text-muted p-3 mb-0">No payments recorded yet.</p>
                  ) : (
                    <Table className="mb-0" responsive>
                      <thead>
                        <tr>
                          <th>Date</th>
                          <th>Type</th>
                          <th className="text-end">Interest</th>
                          <th className="text-end">Capital</th>
                          <th className="text-end">Fees</th>
                          <th />
                        </tr>
                      </thead>
                      <tbody>
                        {l.transactions.map((t) => (
                          <tr key={t.id}>
                            <td>{f.date(t.transactionDate)}</td>
                            <td>{f.label(LOAN_TRANSACTION_TYPES, t.transactionType)}</td>
                            <td className="text-end">
                              <Money pence={t.interestPence} />
                            </td>
                            <td className="text-end">
                              <Money pence={t.capitalPence} />
                            </td>
                            <td className="text-end">
                              <Money pence={t.feesPence} />
                            </td>
                            <td className="text-end">
                              {can("finance:write") && (
                                <ConfirmButton
                                  title="Delete this payment?"
                                  body="Finance cost totals will be updated."
                                  onConfirm={async () => {
                                    await api.delete(`/api/loans/${l.id}/transactions/${t.id}`);
                                    await invalidate();
                                  }}
                                >
                                  <Icon name="trash" size={14} />
                                </ConfirmButton>
                              )}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </Table>
                  )}
                </Section>
                <AuditHistory entityType="loan" entityId={l.id} />
              </Col>
              <Col lg={4}>
                <Attachments entityType="loan" entityId={l.id} title="Offers & statements" />
              </Col>
            </Row>
          </>
        );
      }}
    </QueryState>
  );
}
